import { useState } from 'react'

import { Line } from '../../models/Line'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { Chip } from '../common/fields/Chip'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextArea } from '../common/fields/TextArea'
import { invoiceCategoryOptions } from '../settings/Options'
import { WorkflowIconSection } from './WorkflowIconSection'

type Props = {
    isOpen: boolean
    onClose: () => void
    onDone: (line: Line) => void
    line: Line
}

export const EditExpenseLineModal = ({ isOpen, onClose, onDone, line: defaultLine }: Props) => {
    const [line, setLine] = useState(defaultLine)

    const amount = line.total !== undefined ? (isNaN(line.total) ? 0 : line.total) : 0

    const handleCategoryChange = (option: { id: string; title: string }) => {
        setLine(
            line.clone({
                category: option.id === 'none' ? undefined : option.id,
                memo:
                    (line.memo ?? '').length === 0
                        ? option.id === 'none'
                            ? 'Expense'
                            : `${option.title} expense`
                        : line.memo,
            })
        )
    }

    const handleUpdateMemo = (newMemo: string) => {
        setLine(line.clone({ memo: newMemo }))
    }

    const handleUpdateAllowSalesTax = (allowSalesTax: boolean) => {
        setLine(line.clone({ taxable: allowSalesTax }))
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Edit expense"
            footer={
                <div className="justif-end flex items-center">
                    <Button intent="primary" onClick={() => onDone(line)}>
                        Done
                    </Button>
                </div>
            }
            size="large"
        >
            <div className="my-8 flex flex-col items-stretch justify-start gap-y-8">
                <WorkflowIconSection title="Type" icon="settings.large">
                    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-2">
                        {invoiceCategoryOptions.map((option) => (
                            <Chip
                                key={option.value}
                                title={option.label}
                                size="small"
                                isSelected={line.category === option.value}
                                onClick={() =>
                                    handleCategoryChange({
                                        id: option.value,
                                        title: option.label,
                                    })
                                }
                            />
                        ))}
                    </div>
                </WorkflowIconSection>

                <WorkflowIconSection title="Amount" icon="dollar.large">
                    <CurrencyField
                        type="number"
                        value={amount}
                        onChange={(newValue) => setLine(line.clone({ total: newValue }))}
                        size="medium"
                        step={0.01}
                        min={0}
                        isFullWidth
                    />
                </WorkflowIconSection>

                <WorkflowIconSection
                    title="Memo"
                    icon="note.large"
                    secondaryText={`${(line.item?.memo ?? '').length} / 99`}
                >
                    <TextArea
                        value={line.memo ?? ''}
                        onChange={(event) => handleUpdateMemo(event.target.value)}
                        maxLength={99}
                        className="max-h-[170px] min-h-[74px]"
                        onClear={() => handleUpdateMemo('')}
                        placeholder="Enter a memo"
                        isAutoResizing
                    />
                </WorkflowIconSection>

                <WorkflowIconSection
                    title="Add sales tax"
                    icon="percentage2.large"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox
                            isChecked={line.taxable ?? true}
                            onChange={handleUpdateAllowSalesTax}
                        />
                    }
                />

                <WorkflowIconSection
                    title="Allow discount"
                    icon="discount.large"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox
                            isChecked={line.discountable ?? true}
                            onChange={(newDiscountable) =>
                                setLine(line.clone({ discountable: newDiscountable }))
                            }
                        />
                    }
                />
            </div>
        </Modal>
    )
}
